import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faPlus,
  faTrash,
  faChartColumn,
  faTableCellsLarge,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import PhaseRail from '@frontend/Components/PhaseRail/PhaseRail';
import '@frontend/Components/BidTable/BidTable.css';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import { useBids } from '../useBids';
import { getBid, getBidUuid, setBidWorkbook, updateBid, ensureWorkbook, convertBidToProject, flushBidSaves, hasUnsavedChanges, discardBidChanges, BID_STATUSES, type Bid, type BidStatus } from '../bidStore';
import { getSaveStatusSnapshot } from '../bidSaveStatus';
import SaveStatusIndicator from '../Components/SaveStatusIndicator';
import UnsavedChangesModal from '../Components/UnsavedChangesModal';
import BidDashboard from './BidDashboard';
import MarginPanel from './MarginPanel';
import { useBidWalkthrough, BidWalkthroughOverlay } from './BidWalkthrough';
import {
  PHASE_LIBRARY,
  buildEmptyWorkbookSafe,
  emptyLineItem,
  emptyPhase,
  lineTotal,
  phaseDurationDays,
  phaseManDays,
  phaseManHours,
  phaseTotals,
  weldingJoints,
  weldingProductionPerDay,
  workbookManDays,
  workbookManHours,
  workbookTotals,
  type BidLineItem,
  type BidPhase,
  type BidWorkbook,
  type CalcLineUpsert,
  type LineItemKind,
  type LineItemSource,
  type SubQuote,
} from './bidWorkbook';
import MobLoadsCard from './MobLoadsCard';
import FluidRentalCard from './FluidRentalCard';
import StatusStepper, { BACKWARD_STEP, LOCKED_BID_STATUSES } from './StatusStepper';
// The calculator cards price from the RESOLVED margins (per-bid override ?? company default).
// Fetched once here and shared with both; MarginPanel hands back its save response so a fresh
// override reaches the calculators without a refetch (or a stale rate).
import { fetchMarginParams, type MarginView } from '../bidService';
// Pickers read the bid's FROZEN card, not the live catalog (design §9) — otherwise editing an
// old bid would freeze today's rate onto it and re-price history through the front door.
import { usePhaseTemplateOptions, useRateCardOptions, type PhaseTemplateOption } from '../Admin/useRateCardOptions';
// money/moneyCompact redact when the caller is blind OR the server omitted the figure
import { money, moneyCompact, percent, useFinancialVisible } from '../financialDisplay';
import './BidDetailPage.css';
import '../bidDetailShared.css';
// Subscribing re-renders this tree when the capability resolves — financialDisplay's
// flag is module state, so without a subscriber money would stay redacted forever.
import { useBidCapabilities } from '../useBidCapabilities';
import ClientService from '@frontend/AppSettings/services/ClientService';
import { contactOptions, companyOf } from '../clientContacts';
import BlurCommitInput from '../Components/BlurCommitInput';
import ConvertToProjectModal from './ConvertToProjectModal';
import { AlreadyConvertedError } from '../bidService';
import ToastService from '@frontend/AppSettings/services/ToastService';

const STATUS_VARIANT: Record<BidStatus, 'active' | 'at-risk' | 'in-review' | 'completed' | 'pending' | 'open'> = {
  Draft: 'open',
  'In Review': 'in-review',
  Submitted: 'pending',
  Won: 'active',
  Lost: 'at-risk',
};

const STATUS_COLOR: Record<BidStatus, string> = {
  Draft: '#6c757d',
  'In Review': '#17a2b8',
  Submitted: '#ffc107',
  Won: '#28a745',
  Lost: '#dc3545',
};



const num = (n: number): string => {
  if (!Number.isFinite(n)) return '0';
  return Number(n.toFixed(1)).toLocaleString('en-US');
};

const BidDetailInner = (): JSX.Element => {
  // kicks off the capability fetch; `useFinancialVisible` below is what re-renders when it lands
  useBidCapabilities();
  const pageFinancialVisible = useFinancialVisible();
  const { bidId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Subscribe so external edits re-render; we read the bid header from here.
  const bids = useBids();
  const bid = bids.find((b) => b.id === bidId) ?? getBid(bidId);
  // Status is the edit lock: Submitted / Won / Lost pricing is a RECORD — the server refuses
  // every pricing write (409), and every surface below mirrors it. Computed HERE, before the
  // walkthrough hooks, because the walkthrough rewrites the whole bid and must respect it too.
  const pricingLocked = bid !== undefined && LOCKED_BID_STATUSES.includes(bid.status);

  // Local working copy of the workbook so typing stays snappy and focus is
  // preserved; every mutation also persists to the store.
  const [workbook, setWorkbook] = useState<BidWorkbook>(() =>
    bid?.workbook ?? buildEmptyWorkbookSafe(),
  );
  const [view, setView] = useState<'summary' | string>(() =>
    (bid?.workbook?.phases[0]?.id) ?? 'summary',
  );
  const [tab, setTab] = useState<'dashboard' | 'details'>('dashboard');
  const [phaseToDelete, setPhaseToDelete] = useState<string | null>(null);
  const [confirmWalk, setConfirmWalk] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  // Unsaved-changes prompt shown when leaving the editor with outstanding edits; `leaving` opens it,
  // `savingLeave` shows the spinner while the "Save changes" path writes.
  const [leaving, setLeaving] = useState(false);
  const [savingLeave, setSavingLeave] = useState(false);

  const totals = useMemo(() => workbookTotals(workbook), [workbook]);
  const phaseTemplates = usePhaseTemplateOptions(bidId);

  // Resolved margins for the calculator cards, fetched ONCE and shared (each card fetching its
  // own would multiply the request and let them disagree). Hoisted from the summary view (plan
  // mob-demob-sheet-parity, Build M): the loads card now lives in the MOB PHASE editor, so both
  // views read this one state. Null until loaded — and forever, for a blind caller: the server
  // refuses the data, so the pricing surfaces simply don't exist.
  const calcUuid = getBidUuid(bidId);
  const [calcMargins, setCalcMargins] = useState<MarginView | null>(null);
  // Set once MarginPanel's save has handed back a post-save view: from then on, an in-flight
  // initial GET resolving late must NOT revert the cards to the pre-save rate it fetched —
  // the next Apply would freeze a rate the panel no longer shows (review).
  const marginsSaved = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (!calcUuid || !pageFinancialVisible) return undefined;
    void fetchMarginParams(calcUuid)
      .then((m) => {
        if (!cancelled && m && !marginsSaved.current) setCalcMargins(m);
      })
      // A transient failure leaves the pricing surfaces absent (the MarginPanel idiom);
      // the server stays the boundary and nothing decorative earns a retry panel.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [calcUuid, pageFinancialVisible]);
  const onMarginsSaved = (next: MarginView): void => {
    marginsSaved.current = true;
    setCalcMargins(next);
  };

  // ---- guided walk-through -------------------------------------------------
  const walk = useBidWalkthrough({
    bidId,
    commitWorkbook: (wb) => {
      setWorkbook(wb);
      setBidWorkbook(bidId, wb, workbookTotals(wb).bidTotal);
    },
    setCover: (patch) => updateBid(bidId, patch),
    focusSummary: () => { setTab('details'); setView('summary'); },
    focusPhase: (id) => { setTab('details'); setView(id); },
    focusDashboard: () => setTab('dashboard'),
  });

  // Auto-start when arriving with ?walkthrough=1 (e.g. from the list page).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current) return;
    if (searchParams.get('walkthrough') !== '1') return;
    autoStarted.current = true;
    const params = new URLSearchParams(searchParams);
    params.delete('walkthrough');
    setSearchParams(params, { replace: true });
    // The param is consumed either way, but a LOCKED bid never auto-plays: the walkthrough
    // demotes and rewrites the bid, and a URL must not do that to a record (review).
    if (!pricingLocked) walk.start();
  }, [searchParams, setSearchParams, walk, pricingLocked]);

  // ---- backend hydration ---------------------------------------------------
  // The list load fetches only bid headers; pull this bid's workbook lazily and
  // adopt it into the local editable copy ONCE per bid (never clobbering edits).
  const [loading, setLoading] = useState(true);
  const adoptedForId = useRef<string | null>(null);
  /** Workbook edited locally but not yet written. Null when there is nothing outstanding. */
  const pendingWorkbook = useRef<BidWorkbook | null>(null);

  // Flush on unmount, and when switching to a different bid. Leaving the page never fires blur on
  // the field you were in, so without this the last thing typed is silently discarded. Deps are
  // [bidId] so the cleanup for the OLD bid closes over the old id — it saves to the right bid.
  //
  // Two steps: adopt whatever is typed-but-unwritten into the store (setBidWorkbook), then
  // flushBidSaves to send it IMMEDIATELY rather than waiting out the 5s debounce — the editor is
  // going away, so "autosave in 5s" would never fire.
  useEffect(() => () => {
    const pending = pendingWorkbook.current;
    pendingWorkbook.current = null;
    if (pending) setBidWorkbook(bidId, pending, workbookTotals(pending).bidTotal);
    void flushBidSaves(bidId);
  }, [bidId]);

  // Closing/refreshing the whole tab: an unmount never fires, so this both (a) best-effort flushes
  // the outstanding write — a hard unload cannot await it, but the browser usually lets the
  // in-flight fetch finish — and (b) triggers the browser's NATIVE "Leave site? Changes may not be
  // saved" prompt when there is unsaved work. A custom Discard/Save dialog is not permitted on tab
  // close; SPA navigation (the Back button) gets that dialog via the unsaved-changes modal below.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      const pending = pendingWorkbook.current;
      pendingWorkbook.current = null;
      if (pending) setBidWorkbook(bidId, pending, workbookTotals(pending).bidTotal);
      const unsaved = hasUnsavedChanges(bidId);
      void flushBidSaves(bidId);
      if (unsaved) {
        e.preventDefault();
        e.returnValue = ''; // required for Chrome to show the native confirm
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [bidId]);
  // A FAILED workbook fetch must not degrade to an empty editable workbook: with autosave, the
  // first edit would replace the persisted workbook moments later (gap review 0.4). `loadFailed`
  // swaps the editor for a Retry panel; `retryTick` re-runs this effect on demand.
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void ensureWorkbook(bidId).then((ok) => {
      if (cancelled) return;
      const loaded = getBid(bidId)?.workbook;
      if (loaded && adoptedForId.current !== bidId) {
        adoptedForId.current = bidId;
        setWorkbook(loaded);
        setView(loaded.phases[0]?.id ?? 'summary');
      }
      // Failed AND nothing cached: the persisted workbook is unknown, so no editor. (A cached
      // workbook from an earlier success keeps the editor even if a re-fetch failed.)
      setLoadFailed(!ok && !loaded);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [bidId, retryTick]);

  // (Hooks live HERE, above the loading/not-found returns — never conditional.)
  const [pendingDemote, setPendingDemote] = useState<BidStatus | null>(null);

  const handlePlayWalkthrough = (): void => {
    if (pricingLocked) return;   // the walkthrough rewrites the bid — never on a record
    if (workbook.phases.length > 0) setConfirmWalk(true);
    else walk.start();
  };

  // Hold the loader until this bid's workbook has been fetched, so the editor never renders over
  // a stale/empty workbook that the in-flight fetch would then adopt over (clobbering edits).
  if (loading) {
    return (
      <div className="bid-detail-page">
        <div className="bid-detail-notfound">
          <h2>Loading bid…</h2>
        </div>
      </div>
    );
  }

  if (!bid) {
    return (
      <div className="bid-detail-page">
        <div className="bid-detail-notfound">
          <h2>Bid not found</h2>
          <button className="bid-detail-back-btn" onClick={() => navigate('/bids')}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back to Bids
          </button>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    // NOT an empty editor: the persisted workbook is unknown, and with autosave the first edit in
    // a fallback editor would overwrite it. Retry is the only way forward.
    return (
      <div className="bid-detail-page">
        <div className="bid-detail-notfound">
          <h2>Could not load this bid's workbook</h2>
          <p>Editing is disabled so a save cannot overwrite the estimate on the server.</p>
          <button className="bid-detail-back-btn" onClick={() => setRetryTick((t) => t + 1)}>
            Retry
          </button>
          <button className="bid-detail-back-btn" onClick={() => navigate('/bids')}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back to Bids
          </button>
        </div>
      </div>
    );
  }

  const handleConvert = async (opts: { employeeId?: string; anchorDate?: string }): Promise<void> => {
    if (converting) return;                  // a second click must not start a second conversion
    setConverting(true);
    try {
      // Through the STORE, which maps this bid number to the UUID the route addresses.
      const result = await convertBidToProject(bid.id, opts);
      navigate(`/projectDetails/${result.matterid}`);
    } catch (err) {
      if (err instanceof AlreadyConvertedError) {
        // Not an error worth showing: someone else converted it (or this tab raced). Go to the
        // project they made — that is what the 409 carries the matterid for.
        navigate(`/projectDetails/${err.matterid}`);
        return;
      }
      const status = (err as { status?: number } | null)?.status;
      ToastService.error(
        status === 403
          ? 'You do not have permission to convert this bid.'
          : (err as Error)?.message || 'Could not convert this bid to a project.',
      );
    } finally {
      // Always: if navigate does not unmount this tree, the button would otherwise be stuck
      // disabled at "Converting…" with no way back.
      setConverting(false);
      setConvertOpen(false);
    }
  };

  /** A status transition. Flush FIRST — a trailing debounced workbook save must not fire into
   * the fresh lock and die with a generic toast; then push the transition itself immediately
   * rather than letting it sit in the 5 s debounce. */
  const transitionStatus = (next: BidStatus): void => {
    void flushBidSaves(bid.id).then(() => {
      updateBid(bid.id, { status: next });
      void flushBidSaves(bid.id);
    });
  };

  /** EVERY status control routes here — the stepper, the banner, and the COVER's own select
   * (review: a second select that skipped the flush lost the pricing typed before it, and
   * demoted records with no confirm). Confirmed exactly when the move UNLOCKS pricing
   * (record -> editable status); direct otherwise — In Review -> Draft reopens nothing, and
   * Won -> Submitted moves between records without unlocking anything. */
  const requestStatusChange = (next: BidStatus): void => {
    if (next === bid.status) return;
    if (pricingLocked && !LOCKED_BID_STATUSES.includes(next)) setPendingDemote(next);
    else transitionStatus(next);
  };

  // ---- persistence ---------------------------------------------------------
  // TYPING is local only. Every keystroke used to PUT the ENTIRE workbook, so a 30-character line
  // description was 30 full-workbook saves. Local state still updates per keystroke — that is what
  // keeps the editor responsive and the totals live — but the write waits for `flushWorkbook`.
  const editWorkbook = (next: BidWorkbook): void => {
    if (pricingLocked) return;
    setWorkbook(next);
    pendingWorkbook.current = next;
  };

  /** Write the outstanding edit, if any. Bound to blur on the editor panel below. */
  const flushWorkbook = (): void => {
    const pending = pendingWorkbook.current;
    if (!pending) return;             // nothing typed since the last write — no request
    pendingWorkbook.current = null;
    if (pricingLocked) return;        // a locked bid's workbook never reaches the wire
    setBidWorkbook(bid.id, pending, workbookTotals(pending).bidTotal);
  };

  // STRUCTURAL changes save at once: adding or deleting a phase or a line is a discrete action, not
  // typing, and waiting for a blur that may never come would leave it unsaved.
  const persist = (next: BidWorkbook): void => {
    if (pricingLocked) return;
    setWorkbook(next);
    pendingWorkbook.current = null;
    setBidWorkbook(bid.id, next, workbookTotals(next).bidTotal);
  };

  const patchWorkbook = (patch: Partial<BidWorkbook>): void =>
    editWorkbook({ ...workbook, ...patch });

  const patchPhase = (phaseId: string, patch: Partial<BidPhase>): void =>
    editWorkbook({
      ...workbook,
      phases: workbook.phases.map((p) => (p.id === phaseId ? { ...p, ...patch } : p)),
    });

  // A discrete phase-level ACTION (the welding calculator's Apply/Clear): saves at once, like
  // add/delete — a button click is not typing, and waiting for a blur that may never come would
  // leave the committed pair unsaved.
  const persistPhase = (phaseId: string, patch: Partial<BidPhase>): void =>
    persist({
      ...workbook,
      phases: workbook.phases.map((p) => (p.id === phaseId ? { ...p, ...patch } : p)),
    });

  const patchItem = (
    phaseId: string,
    itemId: string,
    patch: Partial<BidLineItem>,
  ): void =>
    editWorkbook({
      ...workbook,
      phases: workbook.phases.map((p) =>
        p.id === phaseId
          ? { ...p, items: p.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }
          : p,
      ),
    });

  const addItem = (phaseId: string, kind: LineItemKind): void =>
    persist({
      ...workbook,
      phases: workbook.phases.map((p) =>
        p.id === phaseId ? { ...p, items: [...p.items, emptyLineItem(kind)] } : p,
      ),
    });

  const removeItem = (phaseId: string, itemId: string): void =>
    persist({
      ...workbook,
      phases: workbook.phases.map((p) =>
        p.id === phaseId ? { ...p, items: p.items.filter((it) => it.id !== itemId) } : p,
      ),
    });

  /** A pricing calculator's Apply (gap 4.1): each upsert appends its visible line to its phase
   * — or, with `replaceItemId`, updates the line it applied before, in place, keeping its id.
   * BATCHED into one persist (Build M): the loads calculator applies a mob/demob PAIR, and two
   * writes would double-save the replace-all workbook. A discrete action, persisted at once
   * (the welding Apply's discipline: a button click is not typing). */
  const applyCalcLines = (upserts: CalcLineUpsert[], removeItemIds: string[] = []): void => {
    // Defensive (batch review L3): an id both replaced and removed would delete the line just
    // updated — impossible from the current caller, cheap to make impossible forever.
    const replaced = new Set(upserts.map((u) => u.replaceItemId).filter(Boolean));
    const removals = removeItemIds.filter((id) => !replaced.has(id));
    persist({
      ...workbook,
      phases: workbook.phases.map((p) => {
        let items = p.items;
        upserts.forEach(({ phaseId, line, replaceItemId }) => {
          if (p.id !== phaseId) return;
          items = replaceItemId
            ? items.map((it) => (it.id === replaceItemId ? { ...line, id: replaceItemId } : it))
            : [...items, line];
        });
        // The remove list (plan review H1): a calculator superseding its own OLD signature —
        // the legacy combined loads line beside an adopted seed pair — must sweep it in the
        // same persist, or the stale line prices beside the pair forever.
        if (removals.some((id) => items.some((it) => it.id === id))) {
          items = items.filter((it) => !removals.includes(it.id));
        }
        return items === p.items ? p : { ...p, items };
      }),
    });
  };

  const applyCalcLine = (phaseId: string, line: BidLineItem, replaceItemId?: string): void =>
    applyCalcLines([{ phaseId, line, replaceItemId }]);

  const addPhase = (name: string): void => {
    const phase = emptyPhase(name);
    persist({ ...workbook, phases: [...workbook.phases, phase] });
    setView(phase.id);
  };

  /** Add a phase FROM an admin template (gap review 1.2): name, production rate, the configured
   * crew as real lines, and the template id as provenance — the catalog stopped being
   * write-only the day this landed. */
  const addPhaseFromTemplate = (template: PhaseTemplateOption): void => {
    const phase = emptyPhase(template.name);
    phase.phaseTemplateId = template.id;
    if (template.productionPerDayM !== undefined) phase.productionPerDayM = template.productionPerDayM;
    phase.items = template.crew.map((c) => ({
      ...emptyLineItem(c.kind),
      description: c.desc,
      qty: c.qty,
      hrs: c.hrs,
      ...(c.rate !== undefined ? { rate: c.rate } : {}),
    }));
    persist({ ...workbook, phases: [...workbook.phases, phase] });
    setView(phase.id);
  };

  const removePhase = (phaseId: string): void => {
    const remaining = workbook.phases.filter((p) => p.id !== phaseId);
    persist({ ...workbook, phases: remaining });
    if (view === phaseId) setView(remaining[0]?.id ?? 'summary');
    setPhaseToDelete(null);
  };

  // ---- leaving the editor (in-app) -----------------------------------------
  // Try to go back to the list. Adopt any typed-but-unwritten workbook edit into the store first (a
  // Back click can beat the field blur that normally does this), then decide: if anything is still
  // unsaved, ask; otherwise leave straight away (nothing to lose).
  const attemptLeave = (): void => {
    const pending = pendingWorkbook.current;
    if (pending) {
      pendingWorkbook.current = null;
      setBidWorkbook(bidId, pending, workbookTotals(pending).bidTotal);
    }
    if (hasUnsavedChanges(bidId)) setLeaving(true);
    else navigate('/bids');
  };

  const discardAndLeave = (): void => {
    discardBidChanges(bidId);
    setLeaving(false);
    navigate('/bids');
  };

  const saveAndLeave = async (): Promise<void> => {
    setSavingLeave(true);
    try {
      await flushBidSaves(bidId);
    } finally {
      setSavingLeave(false);
    }
    // flushBidSaves never rejects (runSave catches + toasts its own failures), so a failed write
    // surfaces as an 'error' status, not a throw. Only leave once the save actually landed — on
    // failure keep the prompt open so the user sees the error and can retry or discard.
    if (getSaveStatusSnapshot().status === 'error') return;
    setLeaving(false);
    navigate('/bids');
  };

  const activePhase = workbook.phases.find((p) => p.id === view) ?? null;

  return (
    <div className="bid-detail-page">
      {/* ---- Top bar ---- */}
      <div className="bid-detail-topbar">
        <div className="bid-detail-topbar-left">
          <button className="bid-detail-back-button" onClick={attemptLeave} aria-label="Back to bids">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="bid-detail-title-block">
            <div className="bid-detail-title-row">
              <BlurCommitInput
                className="bid-detail-title-input"
                value={bid.name}
                placeholder="Untitled Bid"
                aria-label="Bid name"
                onCommit={(v) => updateBid(bid.id, { name: v })}
              />
              <StatusBadge
                status={bid.status}
                variant={STATUS_VARIANT[bid.status]}
                color={STATUS_COLOR[bid.status]}
                size="medium"
              />
              <StatusStepper bid={bid} onForward={requestStatusChange} onBackward={requestStatusChange} />
            </div>
            <p className="bid-detail-subtitle">
              {bid.id}
              {bid.client ? ` · ${bid.client}` : ''}
              {bid.area ? ` · ${bid.area}` : ''}
              {bid.estimator ? ` · Est. ${bid.estimator}` : ''}
            </p>
          </div>
        </div>

        <div className="bid-detail-topbar-right">
          {/* Only on a Won bid: converting anything else is refused by the server (400), and a
              button that exists only to be rejected is worse than no button. Once converted it
              becomes the way INTO the project — the link is the useful thing at that point. */}
          {bid.convertedMatterid ? (
            <button
              className="bid-detail-walk-btn"
              onClick={() => navigate(`/projectDetails/${bid.convertedMatterid}`)}
              title="This bid has been converted"
            >
              Open Project
            </button>
          ) : bid.status === 'Won' ? (
            <button
              className="bid-detail-walk-btn"
              onClick={() => setConvertOpen(true)}
              disabled={converting}
              title="Create a project from this bid, with a schedule from its phases"
            >
              {converting ? 'Converting…' : 'Convert to Project'}
            </button>
          ) : null}

          <button
            className="bid-detail-walk-btn"
            onClick={handlePlayWalkthrough}
            disabled={walk.state.phase === 'running' || pricingLocked}
            title={pricingLocked
              ? 'The walk-through rewrites this bid — locked while its pricing is a record'
              : 'Watch a bid get filled out end to end'}
          >
            <FontAwesomeIcon icon={faPlay} />
            {walk.state.phase === 'running' ? 'Playing…' : 'Play walk-through'}
          </button>

          <div className="bid-detail-headline">
            <div className="bid-detail-headline-main">
              <span className="bid-detail-headline-label">Total Bid</span>
              <span className="bid-detail-headline-value">{moneyCompact(totals.bidTotal)}</span>
            </div>
            <div className="bid-detail-headline-meta">
              <span>{money(totals.costPerM)}/m</span>
              <span className="bid-detail-dot">•</span>
              <span>Cost {moneyCompact(totals.baseCost)}</span>
              <span className="bid-detail-dot">•</span>
              <span className="bid-detail-profit">{moneyCompact(totals.profit)} profit ({percent(workbook.profitPct)})</span>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Tabs ---- */}
      <div className="bid-detail-tabs">
        <button
          className={`bid-detail-tab${tab === 'dashboard' ? ' is-active' : ''}`}
          onClick={() => setTab('dashboard')}
        >
          <FontAwesomeIcon icon={faChartColumn} /> Dashboard
        </button>
        <button
          className={`bid-detail-tab${tab === 'details' ? ' is-active' : ''}`}
          onClick={() => setTab('details')}
        >
          <FontAwesomeIcon icon={faTableCellsLarge} /> Bid Details
        </button>
        <SaveStatusIndicator />
      </div>

      {tab === 'dashboard' ? (
        <div className="bid-detail-dashboard-wrap">
          <BidDashboard
            bid={bid}
            workbook={workbook}
            onOpenPhase={(id) => { setView(id); setTab('details'); }}
          />
        </div>
      ) : (
      <>
      {/* Status is the edit lock: the banner NAMES the lock and carries the unlock right here
          (the approved design) — nobody gets sent to the list to find out why typing is dead. */}
      {pricingLocked && (
        <div className="bid-locked-banner" role="status">
          <span>
            <strong>{bid.status}</strong> — pricing is locked; the cover facts (outcome, award,
            discount, dates) stay editable.
          </span>
          {!bid.convertedMatterid && BACKWARD_STEP[bid.status] && (
            <button
              className="bid-status-revise"
              onClick={() => requestStatusChange(BACKWARD_STEP[bid.status] as BidStatus)}
            >
              {/* "Revise" only when the single step truly unlocks (Submitted -> In Review);
                  Won/Lost step back to Submitted, still a record — the wording must not
                  promise an unlock that needs a second step (review). */}
              {LOCKED_BID_STATUSES.includes(BACKWARD_STEP[bid.status] as BidStatus)
                ? `Back to ${BACKWARD_STEP[bid.status]}`
                : `Revise — back to ${BACKWARD_STEP[bid.status]}`}
            </button>
          )}
        </div>
      )}
      <div className="bid-detail-body">
        {/* ---- Left rail: phases (the shared PhaseRail — Build R extraction; markup
            byte-pinned by BidRailMarkup.test.tsx) ---- */}
        <PhaseRail
          summaryLabel="Summary"
          summaryFigure={moneyCompact(totals.bidTotal)}
          dividerLabel="Phases"
          activeKey={view}
          onSelect={setView}
          items={workbook.phases.map((p) => ({
            key: p.id,
            name: p.name || 'Untitled phase',
            figure: moneyCompact(phaseTotals(p, workbook.rowLengthM).total),
          }))}
          emptyText="No phases yet. Add one below."
        >
          <div className="bid-detail-add-phase">
            {/* Templates from the bid's frozen snapshot (gap review 1.2) — picking one seeds the
                phase with the admin-configured production rate and crew. PHASE_LIBRARY remains
                only as the fallback when the tenant has no templates (or a pre-P5 bid). */}
            <select
              className="bid-detail-add-select"
              aria-label="Add phase"
              disabled={pricingLocked}
              value=""
              onChange={(e) => {
                const v = e.target.value;
                e.target.value = '';
                if (!v) return;
                if (v.startsWith('t:')) {
                  const template = phaseTemplates.find((t) => t.id === v.slice(2));
                  if (template) addPhaseFromTemplate(template);
                } else {
                  addPhase(v.slice(2) || 'New Phase');
                }
              }}
            >
              <option value="">+ Add phase…</option>
              {phaseTemplates.length > 0
                ? phaseTemplates.map((t) => (
                  <option key={t.id} value={`t:${t.id}`}>{t.name}</option>
                ))
                : PHASE_LIBRARY.map((name) => (
                  <option key={name} value={`n:${name}`}>{name}</option>
                ))}
              <option value="n:New Phase">Custom phase…</option>
            </select>
          </div>
        </PhaseRail>

        {/* ---- Main panel ---- */}
        {/* One handler, not fifteen: React's onBlur is focusout, so it fires whenever ANY field
            inside this panel is left — which is exactly when the edit should be written. */}
        <main className="bid-detail-main" onBlur={flushWorkbook}>
          {view === 'summary' || !activePhase ? (
            <SummaryView
              bid={bid}
              workbook={workbook}
              locked={pricingLocked}
              calcMargins={calcMargins}
              onMarginsSaved={onMarginsSaved}
              onStatusChange={requestStatusChange}
              onBidField={(patch) => updateBid(bid.id, patch)}
              onProfitChange={(v) => patchWorkbook({ profitPct: v })}
              onRowLengthChange={(v) => patchWorkbook({ rowLengthM: v })}
              onOpenPhase={(id) => setView(id)}
              onApplyCalcLine={applyCalcLine}
              onApplyCalcLines={applyCalcLines}
            />
          ) : (
            <PhaseEditor
              phase={activePhase}
              phases={workbook.phases}
              bidId={bid.id}
              rowLengthM={workbook.rowLengthM}
              locked={pricingLocked}
              calcMargins={calcMargins}
              onPhaseField={(patch) => patchPhase(activePhase.id, patch)}
              onPhaseAction={(patch) => persistPhase(activePhase.id, patch)}
              onItemField={(itemId, patch) => patchItem(activePhase.id, itemId, patch)}
              onAddItem={(kind) => addItem(activePhase.id, kind)}
              onRemoveItem={(itemId) => removeItem(activePhase.id, itemId)}
              onDeletePhase={() => setPhaseToDelete(activePhase.id)}
              onApplyCalcLines={applyCalcLines}
            />
          )}
        </main>
      </div>
      </>
      )}

      <ConfirmationModal
        isOpen={pendingDemote !== null}
        onClose={() => setPendingDemote(null)}
        onConfirm={() => {
          if (pendingDemote) transitionStatus(pendingDemote);
          setPendingDemote(null);
        }}
        message={`Move this ${bid.status} bid back to ${pendingDemote ?? ''}? This reopens pricing for editing — the ${bid.status} record keeps its version snapshot.`}
        confirmLabel="Reopen pricing"
        cancelLabel="Cancel"
      />

      <ConvertToProjectModal
        isOpen={convertOpen}
        onClose={() => setConvertOpen(false)}
        onConfirm={handleConvert}
        converting={converting}
        bidName={bid.name}
      />

      <ConfirmationModal
        isOpen={phaseToDelete !== null}
        onClose={() => setPhaseToDelete(null)}
        onConfirm={() => phaseToDelete && removePhase(phaseToDelete)}
        message="Delete this phase and all of its line items? This cannot be undone."
        confirmLabel="Delete Phase"
        cancelLabel="Cancel"
      />

      <ConfirmationModal
        isOpen={confirmWalk}
        onClose={() => setConfirmWalk(false)}
        onConfirm={() => { setConfirmWalk(false); walk.start(); }}
        message="The walk-through rebuilds this bid as a live demo, replacing its current details and phases. Play it?"
        confirmLabel="Play Walk-through"
        cancelLabel="Cancel"
      />

      <BidWalkthroughOverlay
        state={walk.state}
        onStop={walk.stop}
        onReplay={walk.start}
        onClose={walk.stop}
      />

      <UnsavedChangesModal
        isOpen={leaving}
        saving={savingLeave}
        onSave={saveAndLeave}
        onDiscard={discardAndLeave}
        onCancel={() => setLeaving(false)}
      />
    </div>
  );
};

// ===========================================================================
// Outcome card — the COVER's award record (gap 3.7). Rendered for Won/Lost bids, and for any
// bid that already carries outcome data (facts must never go invisible because a status was
// flipped back). The money half — the award figure input and the derived analytics — is gated
// on financial visibility: a blind mutator sees the FACTS (who, how many) but no dollar input,
// because an input hydrated from a stripped payload renders empty and invites an accidental
// clear of a figure its editor cannot see.
// ===========================================================================
const OutcomeCard = ({ bid, onBidField }: { bid: Bid; onBidField: (patch: Partial<Bid>) => void }): JSX.Element | null => {
  const visible = useFinancialVisible();
  // null (an optimistic clear in flight) still counts as data-bearing for BOTH numeric fields,
  // so clearing the last figure on a status-reverted bid doesn't unmount the card mid-edit;
  // the server echo maps null back to undefined and settles it.
  const hasOutcomeData = bid.awardedTo !== '' || bid.awardedValue !== undefined
    || bid.bidderCount !== undefined;
  if (bid.status !== 'Won' && bid.status !== 'Lost' && !hasOutcomeData) return null;

  // Server-derived (never computed here): awarded − our bid, and the per-metre pair.
  const diff = bid.awardDiff;
  const direction = diff === undefined ? null : diff < 0 ? 'under our bid' : diff > 0 ? 'over our bid' : 'at our bid';
  return (
    <div className="bid-cover bid-outcome">
      <h2 className="bid-panel-title">Outcome</h2>
      <p className="bid-panel-subtitle">Who the job went to, for how much, against how many bidders.</p>
      <div className="bid-cover-grid">
        <label className="bid-field">
          <span className="bid-field-label">Awarded To</span>
          <BlurCommitInput className="bid-input" value={bid.awardedTo} placeholder="Winning company (may be us)" onCommit={(v) => onBidField({ awardedTo: v })} />
        </label>
        {visible && (
          <label className="bid-field">
            <span className="bid-field-label">Awarded Value ($)</span>
            <input
              type="number"
              min={0}
              className="bid-input"
              value={bid.awardedValue ?? ''}
              onChange={(e) => {
                // Refuse, never rewrite: min={0} doesn't stop TYPING a minus, and clamping a
                // typed -5 to 0 would commit "awarded at $0" — a fact nobody stated (review).
                // A refused entry is simply not committed; the controlled input snaps back.
                if (e.target.value === '') { onBidField({ awardedValue: null }); return; }
                const parsed = parseFloat(e.target.value);
                if (!Number.isFinite(parsed) || parsed < 0) return;
                onBidField({ awardedValue: parsed });
              }}
            />
          </label>
        )}
        <label className="bid-field">
          <span className="bid-field-label">Number of Bidders</span>
          <input
            type="number"
            min={1}
            className="bid-input"
            value={bid.bidderCount ?? ''}
            onChange={(e) => {
              // Same refusal rule: a typed 0 clamped to 1 would mint "there was 1 bidder", and
              // parseInt truncating 2.5 to 2 is a silent rewrite — refuse both (review). Only ''
              // commits (a clearing null); the schema's ge=1 backs this wall server-side.
              if (e.target.value === '') { onBidField({ bidderCount: null }); return; }
              const parsed = Number(e.target.value);
              if (!Number.isInteger(parsed) || parsed < 1) return;
              onBidField({ bidderCount: parsed });
            }}
          />
        </label>
      </div>
      {visible && diff !== undefined && direction && (
        <p className="bid-outcome-analysis">
          Awarded {money(Math.abs(diff))} {direction}
          {bid.awardedPerM !== undefined && <> · {money(bid.awardedPerM)}/m awarded</>}
          {bid.awardDiffPerM !== undefined && diff !== 0 && <> ({money(Math.abs(bid.awardDiffPerM))}/m)</>}
          {bid.bidderCount ? <> · {bid.bidderCount} bidders</> : null}
        </p>
      )}
    </div>
  );
};

// ===========================================================================
// Summary view — mirrors the workbook SUMMARY sheet, fully live.
// ===========================================================================
interface SummaryViewProps {
  bid: Bid;
  workbook: BidWorkbook;
  /** Status edit-lock: pricing controls disable; the COVER facts on this same view do not. */
  locked: boolean;
  /** The COVER's status select — routed through the page's ONE transition handler (flush-first,
   * confirm-on-unlock), never through the plain field patch (review). */
  onStatusChange: (next: BidStatus) => void;
  onBidField: (patch: Partial<Bid>) => void;
  onProfitChange: (v: number) => void;
  onRowLengthChange: (v: number) => void;
  onOpenPhase: (id: string) => void;
  /** The pricing calculators' Apply (gap 4.1) — appends or updates their visible line. */
  onApplyCalcLine: (phaseId: string, line: BidLineItem, replaceItemId?: string) => void;
  /** The batched form (Build M) — the loads card applies its mob/demob PAIR in one persist,
   * with the sweep of superseded legacy lines declared in the type so no conforming wrapper
   * can silently drop it (batch review L2). */
  onApplyCalcLines: (upserts: CalcLineUpsert[], removeItemIds?: string[]) => void;
  /** Resolved margins, hoisted to the page (Build M) — the loads card also lives in the mob
   * phase editor now, and two fetches could disagree. */
  calcMargins: MarginView | null;
  onMarginsSaved: (next: MarginView) => void;
}
const SummaryView = ({ bid, workbook, locked, calcMargins, onMarginsSaved, onStatusChange, onBidField, onProfitChange, onRowLengthChange, onOpenPhase, onApplyCalcLine, onApplyCalcLines }: SummaryViewProps): JSX.Element => {
  const visible = useFinancialVisible();
  const totals = workbookTotals(workbook);
  // Same query key as the Create Projects/Matters screens, so they share one cache entry.
  const contacts = useQuery({ queryKey: ['Clients'], queryFn: ClientService.getClients });
  return (
    <div className="bid-summary">
      {/* Cover / bid details (maps to the workbook COVER sheet) */}
      <div className="bid-cover">
        <h2 className="bid-panel-title">Bid Details</h2>
        <p className="bid-panel-subtitle">Client, scope and timeline for this bid.</p>
        <div className="bid-cover-grid">
          {/*
            A picker, not the free-text box this used to be. Typing a company here while the bid
            still pointed at a contact elsewhere was silently divergent: every screen showed the
            typed company, and conversion — which prefers the contact — filed the project against a
            person at a DIFFERENT one, with no warning. It is also the only way to give an existing
            bid a contact, which bids created before migration 0008 all need before they can convert.
          */}
          <label className="bid-field">
            <span className="bid-field-label">Client Contact</span>
            <select
              className="bid-input"
              value={bid.clientContactId ?? ''}
              disabled={contacts.isLoading || !!contacts.error}
              onChange={(e) => onBidField({
                clientContactId: e.target.value,
                // Company follows the contact verbatim, so the two can never disagree.
                client: companyOf(contacts.data ?? [], e.target.value),
              })}
            >
              <option value="">
                {contacts.error ? 'Could not load clients' : 'Select client contact...'}
              </option>
              {/* Same treatment as the catalog selects below: a contact missing from the list must
                  still render, or the control silently shows blank and the bid looks unassigned. */}
              {bid.clientContactId
                && !(contacts.data ?? []).some((c) => c.ClientID === bid.clientContactId) && (
                <option value={bid.clientContactId} disabled>
                  {bid.client || 'Client contact'} ({bid.clientContactId})
                </option>
              )}
              {contactOptions(contacts.data ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Client Company</span>
            {/* Read-only: it is derived from the contact above, and an editable copy is exactly how
                the two drifted apart. Still shown because it is what the bid list renders. */}
            <input className="bid-input" value={bid.client} readOnly disabled placeholder="—" />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Area / Location</span>
            <BlurCommitInput className="bid-input" value={bid.area} placeholder="e.g. Northern Region, AB" onCommit={(v) => onBidField({ area: v })} />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Estimator</span>
            <BlurCommitInput className="bid-input" value={bid.estimator} placeholder="e.g. Jordan Avery" onCommit={(v) => onBidField({ estimator: v })} />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Status</span>
            {/* Through the page's transition handler, NOT onBidField: a bare field patch let a
                trailing debounced pricing save die on the fresh lock, and demoted records with
                no confirm (review). One discipline for every status control. */}
            <select className="bid-input" value={bid.status} onChange={(e) => onStatusChange(e.target.value as BidStatus)}>
              {BID_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Created Date</span>
            <input type="date" className="bid-input" value={bid.createdDate} onChange={(e) => onBidField({ createdDate: e.target.value })} />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Due Date</span>
            <input type="date" className="bid-input" value={bid.dueDate} onChange={(e) => onBidField({ dueDate: e.target.value })} />
          </label>
          {/* COVER administrative block (gap 3.8) */}
          <label className="bid-field">
            <span className="bid-field-label">Bid Meeting</span>
            <input type="date" className="bid-input" value={bid.bidMeetingDate} onChange={(e) => onBidField({ bidMeetingDate: e.target.value })} />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Company Rep</span>
            <BlurCommitInput className="bid-input" value={bid.companyRep} placeholder="Our rep at the bid meeting" onCommit={(v) => onBidField({ companyRep: v })} />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Bonds Required</span>
            {/* Tri-state on purpose: "not recorded" is a real answer, distinct from "no" —
                defaulting the select to No would mint a fact nobody stated. */}
            <select
              className="bid-input"
              value={bid.bondsRequired === undefined || bid.bondsRequired === null ? '' : bid.bondsRequired ? 'yes' : 'no'}
              onChange={(e) => onBidField({ bondsRequired: e.target.value === '' ? null : e.target.value === 'yes' })}
            >
              <option value="">Not recorded</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Start Date</span>
            <input type="date" className="bid-input" value={bid.startDate} onChange={(e) => onBidField({ startDate: e.target.value })} />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Completion Date</span>
            <input type="date" className="bid-input" value={bid.completionDate} onChange={(e) => onBidField({ completionDate: e.target.value })} />
          </label>
          {visible && (
            <label className="bid-field">
              <span className="bid-field-label">Discount (%)</span>
              {/* The client discount (Tier 4.4): RECORDED negotiation fact — applied to NO
                  total (promotion to priced waits on the estimators placing it). Money, so
                  the input hides for blind callers like the award figure; refusal semantics
                  match it too ('' clears, out-of-range ignored). */}
              <input
                type="number"
                min={0}
                max={100}
                className="bid-input"
                value={bid.discountPct ?? ''}
                onChange={(e) => {
                  if (e.target.value === '') { onBidField({ discountPct: null }); return; }
                  const parsed = parseFloat(e.target.value);
                  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return;
                  onBidField({ discountPct: parsed });
                }}
              />
            </label>
          )}
          {visible && (
            <label className="bid-field">
              <span className="bid-field-label">Target Margin (%)</span>
              {/* The margin this bid is priced to hit — the first rung of the field-ticket
                  cost resolution (4.3). Shown and typed as PERCENT, stored as the API's 0..1
                  fraction. Money-posture, so it hides for blind callers like the discount —
                  but unlike the discount it is PRICING, so the status lock disables it (the
                  ROW length / profit treatment). Refusal semantics match the discount:
                  '' clears to null, out-of-range is ignored, never clamped. */}
              <input
                type="number"
                min={0}
                max={100}
                className="bid-input"
                disabled={locked}
                value={bid.targetMargin == null ? '' : Math.round(bid.targetMargin * 10000) / 100}
                onChange={(e) => {
                  if (e.target.value === '') { onBidField({ targetMargin: null }); return; }
                  const parsed = parseFloat(e.target.value);
                  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return;
                  onBidField({ targetMargin: parsed / 100 });
                }}
              />
            </label>
          )}
          <label className="bid-field bid-cover-wide">
            <span className="bid-field-label">Other Bidders</span>
            <BlurCommitInput className="bid-input" value={bid.otherBidders} placeholder="e.g. Rival Pipelines, Northern Mainline" onCommit={(v) => onBidField({ otherBidders: v })} />
          </label>
          <label className="bid-field bid-cover-wide">
            <span className="bid-field-label">Scope / Pipe Spec</span>
            <BlurCommitInput className="bid-input" value={bid.scope} placeholder='e.g. 10 km of 10" pipe' onCommit={(v) => onBidField({ scope: v })} />
          </label>
          <label className="bid-field bid-cover-wide">
            <span className="bid-field-label">Description / Notes</span>
            <BlurCommitInput multiline className="bid-input bid-cover-textarea" value={bid.description ?? ''} placeholder="Assumptions, conditions, key details…" onCommit={(v) => onBidField({ description: v })} />
          </label>
        </div>
      </div>

      <OutcomeCard bid={bid} onBidField={onBidField} />

      <div className="bid-summary-header">
        <div>
          <h2 className="bid-panel-title">Estimate Summary</h2>
          <p className="bid-panel-subtitle">Every phase rolls up here automatically.</p>
        </div>
        <div className="bid-summary-controls">
          <label className="bid-field">
            <span className="bid-field-label">ROW Length (m)</span>
            <input
              type="number"
              className="bid-input"
              disabled={locked}
              value={workbook.rowLengthM || ''}
              onChange={(e) => onRowLengthChange(parseFloat(e.target.value) || 0)}
            />
          </label>
          <label className="bid-field">
            <span className="bid-field-label">Profit (%)</span>
            <input
              type="number"
              className="bid-input"
              disabled={locked}
              value={workbook.profitPct || ''}
              onChange={(e) => onProfitChange(parseFloat(e.target.value) || 0)}
            />
          </label>
        </div>
      </div>

      <div className="bid-table-wrap">
        <table className="bid-table bid-summary-table">
          <thead>
            <tr>
              <th>Phase</th>
              <th className="ta-r">Equipment</th>
              <th className="ta-r">Labour</th>
              <th className="ta-r">Subcontract</th>
              {/* A QUANTITY column among money (gap 3.12): renders for every caller — num(),
                  not money() — because a blind reader plans camp off it. */}
              <th className="ta-r">Man-days</th>
              <th className="ta-r">Total</th>
              <th className="ta-r">Cost / m</th>
            </tr>
          </thead>
          <tbody>
            {workbook.phases.map((p) => {
              const t = phaseTotals(p, workbook.rowLengthM);
              return (
                <tr key={p.id} className="bid-summary-row" onClick={() => onOpenPhase(p.id)}>
                  <td className="bid-summary-phase">{p.name || 'Untitled phase'}</td>
                  <td className="ta-r">{money(t.equipment)}</td>
                  <td className="ta-r">{money(t.labour)}</td>
                  <td className="ta-r">{money(t.subcontract)}</td>
                  <td className="ta-r">{num(phaseManDays(p))}</td>
                  <td className="ta-r bold">{money(t.total)}</td>
                  <td className="ta-r muted">{money(t.costPerM)}</td>
                </tr>
              );
            })}
            {workbook.phases.length === 0 && (
              <tr><td colSpan={7} className="bid-table-empty">No phases yet.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td className="bold">Base Cost</td>
              <td className="ta-r">{money(totals.equipment)}</td>
              <td className="ta-r">{money(totals.labour)}</td>
              <td className="ta-r">{money(totals.subcontract)}</td>
              <td className="ta-r bold">{num(workbookManDays(workbook))}</td>
              <td className="ta-r bold">{money(totals.baseCost)}</td>
              <td className="ta-r muted">{money(workbook.rowLengthM > 0 ? totals.baseCost / workbook.rowLengthM : 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="bid-summary-cards">
        <div className="bid-summary-card">
          <span className="bid-summary-card-label">Base Cost</span>
          <span className="bid-summary-card-value">{money(totals.baseCost)}</span>
        </div>
        <div className="bid-summary-card">
          <span className="bid-summary-card-label">Profit ({percent(workbook.profitPct)})</span>
          <span className="bid-summary-card-value">{money(totals.profit)}</span>
        </div>
        <div className="bid-summary-card bid-summary-card-accent">
          <span className="bid-summary-card-label">Total Bid</span>
          <span className="bid-summary-card-value">{money(totals.bidTotal)}</span>
        </div>
        <div className="bid-summary-card">
          <span className="bid-summary-card-label">Cost / metre</span>
          <span className="bid-summary-card-value">{money(totals.costPerM)}</span>
        </div>
        <div className="bid-summary-card">
          {/* The workbook SUMMARY's man-day block (gap 3.12) — real hours off the lines, not
              the sheet's hardcoded × 12. Quantities: rendered for every caller. */}
          <span className="bid-summary-card-label">Man-days</span>
          <span className="bid-summary-card-value">{num(workbookManDays(workbook))}</span>
          <span className="bid-summary-card-sub">{num(workbookManHours(workbook))} man-hours</span>
        </div>
      </div>

      {/* The loads card's HOME is the mob phase editor (Build M — the sheet's own layout);
          the summary keeps it only as the fallback for a bid with no mob-named phase, so the
          recorded counts are never unreachable. */}
      {!workbook.phases.some((p) => /mob/i.test(p.name)) && (
        <MobLoadsCard
          bidId={bid.id}
          phases={workbook.phases}
          margins={calcMargins}
          locked={locked}
          onApplyLines={onApplyCalcLines}
        />
      )}

      <FluidRentalCard
        bidId={bid.id}
        phases={workbook.phases}
        rowLengthM={workbook.rowLengthM}
        margins={calcMargins}
        locked={locked}
        onApplyLine={onApplyCalcLine}
      />

      {/* onSaved: a fresh override reaches the calculator cards without a refetch — otherwise
          an estimator could set "Load $" here and Apply a stale rate in either mount. */}
      <MarginPanel
        bidId={bid.id}
        locked={locked}
        onSaved={onMarginsSaved}
      />
    </div>
  );
};

// ===========================================================================
// Phase editor — strategy block + equipment & labour tables.
// ===========================================================================
interface PhaseEditorProps {
  phase: BidPhase;
  /** ALL phases — the loads card scans the whole workbook for its applied pair (Build M). */
  phases: BidPhase[];
  rowLengthM: number;
  /** Status edit-lock: the WHOLE editor is pricing, so a native fieldset disables it wholesale. */
  locked: boolean;
  /** Resolved margins from the page (Build M) — the mob loads card prices from them here. */
  calcMargins: MarginView | null;
  onPhaseField: (patch: Partial<BidPhase>) => void;
  /** A discrete action's patch (calculator Apply/Clear): persists at once, not on blur. */
  onPhaseAction: (patch: Partial<BidPhase>) => void;
  onItemField: (itemId: string, patch: Partial<BidLineItem>) => void;
  onAddItem: (kind: LineItemKind) => void;
  onRemoveItem: (itemId: string) => void;
  onDeletePhase: () => void;
  /** The loads card's batched Apply (Build M) — the mob/demob pair in one persist, sweep
   * included in the type (batch review L2). */
  onApplyCalcLines: (upserts: CalcLineUpsert[], removeItemIds?: string[]) => void;
  /** bid-number, forwarded to the line-item pickers so they read THIS bid's frozen rate card */
  bidId: string;
}
const PhaseEditor = ({
  phase,
  phases,
  bidId,
  rowLengthM,
  locked,
  calcMargins,
  onPhaseField,
  onPhaseAction,
  onItemField,
  onAddItem,
  onRemoveItem,
  onDeletePhase,
  onApplyCalcLines,
}: PhaseEditorProps): JSX.Element => {
  const financialVisible = useFinancialVisible();
  const t = phaseTotals(phase, rowLengthM);
  const duration = phaseDurationDays(phase);
  const equipment = phase.items.filter((it) => it.kind === 'equipment');
  const labour = phase.items.filter((it) => it.kind === 'labour');

  return (
    <div className="bid-phase">
      {/* Everything in a phase prices, so the lock is one native fieldset: every input, select
          and button inside disables together — no per-control bookkeeping to fall out of date. */}
      <fieldset className="bid-locked-fieldset" disabled={locked}>
      <div className="bid-phase-header">
        <input
          className="bid-phase-name-input"
          value={phase.name}
          placeholder="Phase name"
          onChange={(e) => onPhaseField({ name: e.target.value })}
        />
        <button className="bid-phase-delete" onClick={onDeletePhase}>
          <FontAwesomeIcon icon={faTrash} /> Delete phase
        </button>
      </div>

      {/* Strategy */}
      <div className="bid-strategy">
        <label className="bid-field">
          <span className="bid-field-label">Total Distance (m)</span>
          <input
            type="number"
            className="bid-input"
            value={phase.distanceM || ''}
            onChange={(e) => onPhaseField({ distanceM: parseFloat(e.target.value) || 0 })}
          />
        </label>
        <label className="bid-field">
          <span className="bid-field-label">Production / Day (m)</span>
          <input
            type="number"
            className="bid-input"
            value={phase.productionPerDayM || ''}
            onChange={(e) => onPhaseField({ productionPerDayM: parseFloat(e.target.value) || 0 })}
          />
        </label>
        <div className="bid-field">
          <span className="bid-field-label">Duration (days)</span>
          <div className="bid-derived">{num(duration)}</div>
        </div>
        {financialVisible && (
          <label className="bid-field">
            <span className="bid-field-label">Profit % override</span>
            {/* Sheet tier of the three-level margin resolution (gap review 1.3/1.4): stored and
                resolved, not applied to totals until the WGL engine. Financial-only — the server
                strips it for blind readers, so rendering an input would wipe it on their save. */}
            <input
              type="number"
              className="bid-input"
              value={phase.profitRateOverride ?? ''}
              onChange={(e) => onPhaseField({
                profitRateOverride: e.target.value === '' ? undefined : parseFloat(e.target.value),
              })}
            />
          </label>
        )}
        <label className="bid-field bid-field-grow">
          <span className="bid-field-label">Special Conditions</span>
          <input
            className="bid-input"
            value={phase.specialConditions}
            placeholder="Notes / assumptions for this phase"
            onChange={(e) => onPhaseField({ specialConditions: e.target.value })}
          />
        </label>
      </div>

      <WeldingCalculator key={phase.id} phase={phase} onPhaseAction={onPhaseAction} />

      <SubQuotesPanel phase={phase} onPhaseField={onPhaseField} onPhaseAction={onPhaseAction} />

      {/* The MOBE-DEMOBE sheet's own layout (Build M): the load matrix lives IN the mob phase,
          and its Apply prices the sheet's two blocks as this phase's own lines. Name-matched —
          the codebase's one mob discrimination — and open: the matrix IS this phase's content. */}
      {/mob/i.test(phase.name) && (
        <details className="bid-welding-calc" open>
          <summary className="bid-welding-calc-summary">Mobilization / Demobilization loads</summary>
          <MobLoadsCard
            bidId={bidId}
            phases={phases}
            phaseId={phase.id}
            margins={calcMargins}
            locked={locked}
            onApplyLines={onApplyCalcLines}
          />
        </details>
      )}

      <LineItemTable
        title="Equipment"
        kind="equipment"
        bidId={bidId}
        items={equipment}
        onItemField={onItemField}
        onRemoveItem={onRemoveItem}
        onAddItem={() => onAddItem('equipment')}
      />
      <LineItemTable
        title="Labour"
        kind="labour"
        bidId={bidId}
        items={labour}
        onItemField={onItemField}
        onRemoveItem={onRemoveItem}
        onAddItem={() => onAddItem('labour')}
      />

      {/* Phase footer */}
      <div className="bid-phase-footer">
        <div className="bid-phase-foot-item"><span>Equipment</span><strong>{money(t.equipment)}</strong></div>
        <div className="bid-phase-foot-item"><span>Labour</span><strong>{money(t.labour)}</strong></div>
        <div className="bid-phase-foot-item"><span>Subcontract</span><strong>{money(t.subcontract)}</strong></div>
        {/* Quantities (gap 3.12) — num(), not money(): a blind reader keeps their crew counts. */}
        <div className="bid-phase-foot-item"><span>Man-days</span><strong>{num(phaseManDays(phase))}</strong></div>
        <div className="bid-phase-foot-item"><span>Man-hours</span><strong>{num(phaseManHours(phase))}</strong></div>
        <div className="bid-phase-foot-item bid-phase-foot-total"><span>Phase Total</span><strong>{money(t.total)}</strong></div>
        <div className="bid-phase-foot-item"><span>Cost / m</span><strong>{money(t.costPerM)}</strong></div>
      </div>
      </fieldset>
    </div>
  );
};

// ===========================================================================
// Sub-quote comparison (gap 3.9) — the CLEARING (SUB) sheet: several subs quote a phase, each
// with a value and qualifiers; one is marked successful. DECISION SUPPORT only: awarding never
// writes a line or a rate — the workbook lines stay the priced truth.
//
// Field edits commit like line items (local per keystroke, flushed on blur); add/remove/award
// are discrete actions and persist at once. The value column and the vs-low comparison are
// financial-gated: quote values are vendor money (stripped server-side), and an input hydrated
// from a stripped payload invites an accidental wipe. Awarding an UNPRICED quote is
// un-clickable — the schema refuses it server-side, and that 400 would fail the whole save.
//
// KNOWN EXPOSURE (recorded, not constructible today): a MUTATE_BIDS-without-SEE_FINANCIALS
// caller's round-trip save carries stripped (absent) quote values — a silent value wipe like
// the margin overrides' recorded exposure, EXCEPT while an award exists, where the
// "successful must be priced" wall turns it into a hard 400 on every save (louder, arguably
// safer). Every MUTATE_BIDS role also holds SEE_FINANCIALS in today's matrix (roles.py); if
// that pairing ever changes, this panel and the overrides need a server-side belt together.
// ===========================================================================
const SubQuotesPanel = ({ phase, onPhaseField, onPhaseAction }: {
  phase: BidPhase;
  onPhaseField: (patch: Partial<BidPhase>) => void;
  onPhaseAction: (patch: Partial<BidPhase>) => void;
}): JSX.Element => {
  const visible = useFinancialVisible();
  const quotes = phase.subQuotes ?? [];
  const priced = quotes.filter((q) => q.quoteValue !== undefined);
  const cheapest = priced.length ? Math.min(...priced.map((q) => q.quoteValue as number)) : undefined;

  const patchQuote = (i: number, patch: Partial<SubQuote>): void =>
    onPhaseField({ subQuotes: quotes.map((q, j) => (j === i ? { ...q, ...patch } : q)) });

  return (
    <details className="bid-sub-quotes" open={quotes.length > 0}>
      <summary className="bid-welding-calc-summary">
        Sub quotes{quotes.length > 0 ? ` (${quotes.length})` : ''}
      </summary>
      <div className="bid-sub-quotes-body">
        {quotes.length > 0 && (
          <div className="bid-table-wrap">
            <table className="bid-table bid-sub-quotes-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  {visible && <th className="ta-r">Quote</th>}
                  {visible && <th className="ta-r">vs low</th>}
                  <th>Qualifiers</th>
                  <th>Successful</th>
                  <th aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {quotes.map((q, i) => (
                  // Index keys: rows only append/remove, never reorder.
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i} className={q.isSuccessful ? 'is-successful' : ''}>
                    <td>
                      <input
                        className="bid-input"
                        aria-label="Quote vendor"
                        value={q.vendor}
                        placeholder="e.g. North Clearing"
                        onChange={(e) => patchQuote(i, { vendor: e.target.value })}
                      />
                      {/* Vendorless rows are filtered off the wire (the schema refuses them,
                          and one would 400 the whole workbook save) — say so, or the row
                          silently vanishes on the next refresh. */}
                      {!q.vendor.trim() && (
                        <div className="bid-sub-quote-hint">Not saved until a vendor is named</div>
                      )}
                    </td>
                    {visible && (
                      <td className="ta-r">
                        <input
                          type="number"
                          min={0}
                          className="bid-input ta-r"
                          aria-label="Quote value"
                          value={q.quoteValue ?? ''}
                          onChange={(e) => {
                            // '' = unpriced (a real awaiting state, never $0); negatives refused,
                            // not clamped. An AWARDED price refuses the clear entirely: '' also
                            // fires transiently mid-retype (backspace-to-empty, a lone minus),
                            // and un-awarding on it silently destroyed the award while the user
                            // was just correcting a digit (review). Un-award via Clear award.
                            if (e.target.value === '') {
                              if (!q.isSuccessful) patchQuote(i, { quoteValue: undefined });
                              return;
                            }
                            const parsed = parseFloat(e.target.value);
                            if (!Number.isFinite(parsed) || parsed < 0) return;
                            patchQuote(i, { quoteValue: parsed });
                          }}
                        />
                      </td>
                    )}
                    {visible && (
                      <td className="ta-r">
                        {q.quoteValue === undefined || cheapest === undefined ? (
                          <span className="muted">—</span>
                        ) : q.quoteValue === cheapest ? (
                          <span className="bid-sub-quote-low">Low</span>
                        ) : (
                          <span className="muted">+{money(q.quoteValue - cheapest)}</span>
                        )}
                      </td>
                    )}
                    <td>
                      <input
                        className="bid-input"
                        aria-label="Quote qualifiers"
                        value={q.qualifiers}
                        placeholder="Exclusions, conditions…"
                        onChange={(e) => patchQuote(i, { qualifiers: e.target.value })}
                      />
                    </td>
                    <td className="ta-c">
                      <input
                        type="radio"
                        name={`sub-quote-successful-${phase.id}`}
                        aria-label={`Mark ${q.vendor || 'quote'} successful`}
                        checked={q.isSuccessful}
                        // An unpriced award is refused server-side; un-clickable here.
                        disabled={q.quoteValue === undefined}
                        onChange={() => onPhaseAction({
                          subQuotes: quotes.map((qq, j) => ({ ...qq, isSuccessful: j === i })),
                        })}
                      />
                    </td>
                    <td>
                      <button
                        className="bid-phase-delete"
                        aria-label={`Remove ${q.vendor || 'quote'}`}
                        onClick={() => onPhaseAction({
                          subQuotes: quotes.filter((_, j) => j !== i),
                        })}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="bid-sub-quotes-actions">
          <button
            className="bid-add-row"
            onClick={() => onPhaseAction({
              subQuotes: [...quotes, { vendor: '', qualifiers: '', isSuccessful: false }],
            })}
          >
            <FontAwesomeIcon icon={faPlus} /> Add quote
          </button>
          {quotes.some((q) => q.isSuccessful) && (
            <button
              className="bid-phase-delete"
              onClick={() => onPhaseAction({
                subQuotes: quotes.map((q) => ({ ...q, isSuccessful: false })),
              })}
            >
              Clear award
            </button>
          )}
        </div>
      </div>
    </details>
  );
};

// ===========================================================================
// Welding productivity calculator (gap 3.10) — job footage + joint length + welds/day derive
// the production rate the schedule runs on: production/day = joint length × welds/day;
// joints = distance / joint length (rounded up — a part-length joint is still a joint to weld).
//
// DRAFT-then-Apply, not per-keystroke commits: the two inputs and the production rate they imply
// land on the phase in ONE patch, so a half-typed state never reaches the wire (the schema
// refuses one input without the other). The stored production_per_day_m stays authoritative —
// hand-editing it later supersedes the saved inputs without contradiction.
// ===========================================================================
const WeldingCalculator = ({ phase, onPhaseAction }: {
  phase: BidPhase;
  onPhaseAction: (patch: Partial<BidPhase>) => void;
}): JSX.Element => {
  // Seeded from the saved state; keyed on phase.id by the caller so switching phases reseeds.
  const [jointDraft, setJointDraft] = useState<string>(
    phase.jointLengthM !== undefined ? String(phase.jointLengthM) : '',
  );
  const [weldsDraft, setWeldsDraft] = useState<string>(
    phase.weldsPerDay !== undefined ? String(phase.weldsPerDay) : '',
  );

  const joint = parseFloat(jointDraft);
  const welds = parseFloat(weldsDraft);
  // MIRRORS the schema walls (workbook_schemas: ge 0.001 / le 1000, ge 0.01 / le 10 000) —
  // an Apply the server would 400 must be un-clickable here, because that 400 fails the WHOLE
  // workbook save and drops every coalesced pending edit with a generic toast (review).
  const valid = Number.isFinite(joint) && joint >= 0.001 && joint <= 1000
    && Number.isFinite(welds) && welds >= 0.01 && welds <= 10_000;
  const production = valid ? weldingProductionPerDay(joint, welds) : 0;
  const joints = valid ? weldingJoints(phase.distanceM, joint) : 0;

  const apply = (): void => {
    if (!valid) return;
    // One patch, persisted at once: the rate AND the inputs that produced it — the pair is
    // never split (the schema refuses half a state), and a button click is not typing.
    onPhaseAction({ productionPerDayM: production, jointLengthM: joint, weldsPerDay: welds });
  };

  const clear = (): void => {
    setJointDraft('');
    setWeldsDraft('');
    // Clears the saved inputs; the production rate they once computed stays — it is the
    // schedule's stored fact, and erasing provenance must not erase the rate.
    onPhaseAction({ jointLengthM: undefined, weldsPerDay: undefined });
  };

  return (
    <details className="bid-welding-calc" open={phase.jointLengthM !== undefined}>
      <summary className="bid-welding-calc-summary">Welding productivity calculator</summary>
      <div className="bid-welding-calc-body">
        <label className="bid-field">
          <span className="bid-field-label">Joint length (m)</span>
          <input
            type="number"
            min={0}
            className="bid-input"
            value={jointDraft}
            placeholder="e.g. 18"
            onChange={(e) => setJointDraft(e.target.value)}
          />
        </label>
        <label className="bid-field">
          <span className="bid-field-label">Welds / day</span>
          <input
            type="number"
            min={0}
            className="bid-input"
            value={weldsDraft}
            placeholder="e.g. 60"
            onChange={(e) => setWeldsDraft(e.target.value)}
          />
        </label>
        <div className="bid-field">
          <span className="bid-field-label">Joints</span>
          <div className="bid-derived">{valid && joints > 0 ? joints.toLocaleString('en-US') : '—'}</div>
        </div>
        <div className="bid-field">
          <span className="bid-field-label">Production / day</span>
          <div className="bid-derived">{valid ? `${num(production)} m` : '—'}</div>
        </div>
        <div className="bid-field">
          <span className="bid-field-label">Days</span>
          <div className="bid-derived">
            {valid && production > 0 && phase.distanceM > 0 ? num(phase.distanceM / production) : '—'}
          </div>
        </div>
        <div className="bid-welding-calc-actions">
          <button className="bid-add-row" onClick={apply} disabled={!valid}>
            Apply to production rate
          </button>
          {(phase.jointLengthM !== undefined || jointDraft || weldsDraft) && (
            <button className="bid-phase-delete" onClick={clear}>Clear</button>
          )}
        </div>
      </div>
    </details>
  );
};

// ===========================================================================
// Reusable line-item table (equipment or labour).
// ===========================================================================
interface LineItemTableProps {
  title: string;
  kind: LineItemKind;
  /** bid-number; the picker resolves it to the UUID its rate card is addressed by */
  bidId: string;
  items: BidLineItem[];
  onItemField: (itemId: string, patch: Partial<BidLineItem>) => void;
  onRemoveItem: (itemId: string) => void;
  onAddItem: () => void;
}
const LineItemTable = ({ title, kind, bidId, items, onItemField, onRemoveItem, onAddItem }: LineItemTableProps): JSX.Element => {
  const rateLabel = kind === 'labour' ? 'Rate/hr' : 'Rate';
  // Reference catalogs (P3). Active rows only, so a deactivated item leaves the picker while any
  // line already using it keeps the rate frozen onto that line.
  const catalogOptions = useRateCardOptions(bidId, kind === 'labour' ? 'labour' : 'equipment');
  // The bid's frozen cost codes (same snapshot discipline as the rate pickers) — the id
  // round-tripped through DB/schema/store since P3 but the estimator had no way to SET it
  // (gap review 1.6), so every baseline bucketed under "(uncoded)".
  const costCodeOptions = useRateCardOptions(bidId, 'cost-codes');
  const idField = kind === 'labour' ? 'labourRoleId' : 'equipmentItemId';

  /** Picking a catalog item copies its name + rate onto the line and records which item it was. */
  const pickCatalogItem = (it: BidLineItem, optionId: string): void => {
    if (!optionId) {
      onItemField(it.id, { [idField]: undefined } as Partial<BidLineItem>);
      return;
    }
    const option = catalogOptions.find((o) => o.id === optionId);
    if (!option) return;
    onItemField(it.id, {
      [idField]: option.id,
      description: option.label,
      // freeze the catalog rate onto the line; leave a hand-entered rate alone if the
      // catalog item has none
      ...(option.rate !== undefined ? { rate: option.rate } : {}),
    } as Partial<BidLineItem>);
  };
  return (
    <div className="bid-items">
      <div className="bid-items-head">
        <h3 className="bid-items-title">{title}</h3>
        <button className="bid-add-row" onClick={onAddItem}>
          <FontAwesomeIcon icon={faPlus} /> Add {kind === 'labour' ? 'labour' : 'equipment'}
        </button>
      </div>
      <div className="bid-table-wrap">
        <table className="bid-table bid-items-table">
          <thead>
            <tr>
              <th className="col-ref">Catalog</th>
              <th className="col-desc">Description</th>
              <th className="ta-r col-n">Qty</th>
              <th className="ta-r col-n">Hrs</th>
              <th className="ta-r col-n">Days</th>
              <th className="ta-r col-n">{rateLabel}</th>
              <th className="col-src">Source</th>
              <th className="col-ref">Cost code</th>
              <th className="ta-r col-total">Total</th>
              <th className="col-x" aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className={it.source === 'sub' ? 'is-sub' : ''}>
                <td className="col-ref">
                  {/* aria-label is deliberately STABLE across rows and across picking. Interpolating
                      the description would make the control's accessible name change the moment it
                      is used — losing a screen-reader user's place (and any test's locator). */}
                  <select
                    className="bid-cell-select"
                    aria-label={`${kind === 'labour' ? 'Labour role' : 'Equipment'} catalog item`}
                    value={(it[idField] as string | undefined) ?? ''}
                    onChange={(e) => pickCatalogItem(it, e.target.value)}
                  >
                    <option value="">Custom…</option>
                    {catalogOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                    {/* A line can reference an item that has since been deactivated. Without a
                        matching option the select would fall back to "Custom…", hiding the
                        provenance of a frozen rate exactly when it matters most. */}
                    {it[idField] && !catalogOptions.some((o) => o.id === it[idField]) && (
                      <option value={it[idField] as string} disabled>
                        {it.description || 'Catalog item'} (retired)
                      </option>
                    )}
                  </select>
                </td>
                <td className="col-desc">
                  <input
                    className="bid-cell-input"
                    value={it.description}
                    placeholder="Description"
                    onChange={(e) => onItemField(it.id, { description: e.target.value })}
                  />
                </td>
                <td className="col-n"><NumCell value={it.qty} onChange={(v) => onItemField(it.id, { qty: v })} /></td>
                <td className="col-n"><NumCell value={it.hrs} onChange={(v) => onItemField(it.id, { hrs: v })} /></td>
                <td className="col-n"><NumCell value={it.days} onChange={(v) => onItemField(it.id, { days: v })} /></td>
                <td className="col-n"><NumCell value={it.rate} onChange={(v) => onItemField(it.id, { rate: v })} /></td>
                <td className="col-src">
                  <select
                    className="bid-cell-select"
                    value={it.source}
                    onChange={(e) => onItemField(it.id, { source: e.target.value as LineItemSource })}
                  >
                    <option value="inhouse">In-house</option>
                    <option value="sub">Sub</option>
                  </select>
                </td>
                <td className="col-ref">
                  <select
                    className="bid-cell-select"
                    aria-label="Cost code"
                    value={it.costCodeId ?? ''}
                    onChange={(e) => onItemField(it.id, { costCodeId: e.target.value || undefined })}
                  >
                    <option value="">—</option>
                    {costCodeOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                    {it.costCodeId && !costCodeOptions.some((o) => o.id === it.costCodeId) && (
                      <option value={it.costCodeId} disabled>Code (retired)</option>
                    )}
                  </select>
                </td>
                <td className="ta-r col-total bold">{money(lineTotal(it))}</td>
                <td className="col-x">
                  <button className="bid-row-remove" aria-label="Remove row" onClick={() => onRemoveItem(it.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="bid-table-empty">No {title.toLowerCase()} yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const NumCell = ({ value, onChange }: { value: number; onChange: (v: number) => void }): JSX.Element => (
  <input
    type="number"
    className="bid-cell-input ta-r"
    value={value === 0 ? '' : value}
    placeholder="0"
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
  />
);

// Key by bidId so switching between bids always remounts with fresh state.
const BidDetailPage = (): JSX.Element => {
  const { bidId = '' } = useParams();
  return <BidDetailInner key={bidId} />;
};

export default BidDetailPage;
